import { FC, useEffect } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from '../../../../shared/hooks/redux';
import { Header } from '../../../../components/header';
import { InnsendingLinje } from '../../../../components/Header4';
import { translateWhenType } from '../../../../core/helpers/translate';
import { resetStates } from '../../../../core/redux/containerOrderState';
import { usePostOrderMutation, useGetProjectsQuery } from '../../../../core/redux/transport';
import { NewLoad } from '../../../../core/types/transport/api/newLoad';
import { NewOrder } from '../../../../core/types/transport/api/newOrder';
import { When } from '../../../../core/types/transport/when';
import { CancelButton } from '../../../../shared/components/CancelButton';
import { Content } from '../../../../shared/components/Content';
import { dateWithoutTimezone } from '../../../../shared/logic/dates';
import { PageUnauthenticated } from '../../../../shared/components/page';
import { emptyGuard } from '../../../../core/helpers/functions';

export const ContainerEmptySubmitPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sendOrder, sendOrderResponse] = usePostOrderMutation();
  const { data: projects } = useGetProjectsQuery();
  const {
    projectTo,
    deliveryTimeType,
    startTime,
    endTime,
    deliveryDate,
    isEarlyDelivery,
    isImportantDeadLine,
    timerCounter,
    orderComment,
    contactPerson,
    containers,
  } = useSelector((s) => s.containerOrderState);

  useEffect(() => {
    if (projectTo === null) {
      navigate('/container');
    }
  }, [projectTo]);

  useEffect(() => {
    if (sendOrderResponse.isSuccess) {
      navigate(`/bestilling/bekreftelse/${sendOrderResponse.data.id}`);
    }
  }, [sendOrderResponse.isSuccess]);

  useEffect(() => {
    if (sendOrderResponse.isError) {
      navigate('/container/inn/innsending');
    }
  }, [sendOrderResponse.isError]);

  if (projectTo === null) {
    return null;
  }

  function getWhen(): When {
    const timeWhen = translateWhenType(deliveryTimeType.time);
    if (timeWhen === 'Custom') {
      return {
        when: timeWhen,
        from: startTime ? startTime.toISOString() : undefined,
        to: endTime ? endTime.toISOString() : undefined,
      };
    }
    return {
      when: timeWhen,
    };
  }

  const project = projects?.find((e) => e.id === projectTo);

  const contactId: number | undefined = contactPerson?.employeeNumber || undefined;
  const contactName: string | undefined = contactPerson?.name || undefined;
  const contactNumber: string | undefined = contactPerson?.number || undefined;

  const postOrder = () => {
    if (!deliveryDate.day) {
      throw new Error('Leveringstid er ikke spesifisert');
    }
    const orderToSend: NewOrder = {
      type: 'ContainerEmptying',
      fromProject: projectTo,
      toProject: projectTo,
      date: deliveryDate.day && dateWithoutTimezone(deliveryDate.day),
      when: getWhen(),
      loads:
        containers.map((c): NewLoad => ({
          containerExternal: c.isExternal ? c.containerExternal : undefined,
          containerInternalNumber: c.isExternal ? undefined : `${c?.containerInternalNumber?.id}`,
          amount: 1,
          wasteType: emptyGuard(c.mass?.label),
        })),
      earlyDelivery: isEarlyDelivery,
      importantDeadline: isImportantDeadLine,
      extraTimeInFacility: timerCounter || 0,
      comment: orderComment || '',
      contactPersonEmployeeNumber: contactId,
      contactPersonName: contactName,
      contactPersonPhoneNumber: contactNumber,
    };
    sendOrder(orderToSend);
  };

  function checkIfImportantDeadline() {
    return (
      <h5>
        Viktig Tidsfrist!
      </h5>
    );
  }

  function displayTime() {
    if (deliveryTimeType.type < 5) {
      return (
        <div>
          {deliveryTimeType.time}
        </div>
      );
    }
    return (
      <div>
        <div>
          {`Tidligst: ${startTime && format(startTime, 'HH:mm')} `}
        </div>
        <div>
          {`Senest: ${endTime && format(endTime, 'HH:mm')} `}
        </div>
      </div>
    );
  }

  return (
    <PageUnauthenticated className="innsending-page parent-pos">
      <Header>Containerbestilling</Header>
      <Content>
        <InnsendingLinje text="Steg 4 - Innsending" styling="page-item" />
        <div className="extra-inline-padding">
          <InnsendingLinje text="Din bestilling er klar til innsending" />
          <p>
            Les gjennom oppsummeringen under og sjekk at informasjonen stemmer før du sender
          </p>
          <InnsendingLinje text="Container tømming" />
          <InnsendingLinje text="Prosjekt" />
          <div>
            {`${project?.id} ${project?.projectName}`}
          </div>
          <InnsendingLinje text={containers.length > 1 ? 'Containere' : 'Container'} />
          <Box display="flex" flexDirection="column" gap={1}>
            {containers.map((c) => (
              <span>{`${c.isExternal ? `(ekstern) ${c.containerExternal}` : c.containerInternalNumber?.label} ${c.mass?.id !== 0 ? `med ${c.mass?.label}` : '(tom)'}`}</span>
            ))}
          </Box>
          <InnsendingLinje text="Leveringsdato" />
          <div>
            {`${deliveryDate.day && format(deliveryDate.day, 'dd.MM.yyyy')}`}
          </div>
          <InnsendingLinje text="Kan leveres dagen før" />
          <div>
            {isEarlyDelivery ? 'Ja' : 'Nei'}
          </div>
          <InnsendingLinje text="Leveringstidspunkt" />
          {displayTime()}
          {isImportantDeadLine && checkIfImportantDeadline()}
          <InnsendingLinje text="Ekstra tid i anlegg" />
          <div>
            {`${timerCounter || 0} time(r)`}
          </div>
          <InnsendingLinje text="Kontaktperson" />
          <div>
            {`${contactPerson?.name} - ${contactPerson?.number}`}
          </div>
          <div>
            <InnsendingLinje text="Kommentar" />
          </div>
          <div className="display-linebreak">
            {orderComment}
          </div>
        </div>
        <div className="bottom-inner">
          <div className="bottom-element">
            <Button
              className="left-element"
              onClick={() => navigate(-1)}
              variant="contained"
              size="large"
            >
              Tilbake
            </Button>
            <CancelButton confirm={() => {
              dispatch(resetStates());
              return navigate('/container');
            }}
            />
            <Button
              className="right-element"
              disabled={sendOrderResponse.isLoading}
              onClick={postOrder}
              variant="contained"
              size="large"
            >
              Send inn
            </Button>
            {sendOrderResponse.isLoading && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />
            )}
          </div>
        </div>
      </Content>
    </PageUnauthenticated>
  );
};
